/**
 * CallsWidget Component
 *
 * Виджет для управления звонками с организацией.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Group,
  Text,
  Button,
  Box,
  Stack,
  ActionIcon,
  Menu,
  Loader,
  Center,
  Avatar,
  Badge,
} from '@mantine/core';
import {
  IconPhone,
  IconPlus,
  IconDots,
  IconPencil,
  IconTrash,
  IconClock,
  IconUser,
  IconEdit,
  IconCheck,
  IconX,
  IconBan,
  IconArrowRight,
  IconCalendarEvent,
  IconClockPlay,
} from '@tabler/icons-react';
import BaseWidget from './BaseWidget';
import CallFormModal from './CallFormModal';
import CallViewModal from './CallViewModal';
import EditReasonModal from './EditReasonModal';
import CallCompleteModal from './CallCompleteModal';
import CallRescheduleModal from './CallRescheduleModal';
import CallCancelModal from './CallCancelModal';
import tokens from '../../../theme/designTokens';

/**
 * Конфигурация статусов звонков
 */
const STATUS_CONFIG = {
  planned: {
    label: 'Запланирован',
    color: 'blue',
    icon: IconClockPlay,
  },
  completed: {
    label: 'Выполнен',
    color: 'green',
    icon: IconCheck,
  },
  missed: {
    label: 'Пропущен',
    color: 'red',
    icon: IconX,
  },
  cancelled: {
    label: 'Отменён',
    color: 'gray',
    icon: IconBan,
  },
  rescheduled: {
    label: 'Перенесён',
    color: 'orange',
    icon: IconArrowRight,
  },
};

/**
 * API функции для звонков
 */
const callsApi = {
  async getCalls(organizationId) {
    const response = await fetch(`/api/organizations/${organizationId}/calls`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить звонки');
    }
    return response.json();
  },

  async createCall(organizationId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/calls`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось создать звонок');
    }
    return response.json();
  },

  async updateCall(organizationId, callId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/calls/${callId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось обновить звонок');
    }
    return response.json();
  },

  async deleteCall(organizationId, callId) {
    const response = await fetch(`/api/organizations/${organizationId}/calls/${callId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось удалить звонок');
    }
  },

  async getContacts(organizationId) {
    const response = await fetch(`/api/organizations/${organizationId}/contacts`, {
      credentials: 'include',
    });
    if (!response.ok) {
      throw new Error('Не удалось загрузить контакты');
    }
    return response.json();
  },

  async completeCall(organizationId, callId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/calls/${callId}/complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось отметить звонок выполненным');
    }
    return response.json();
  },

  async rescheduleCall(organizationId, callId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/calls/${callId}/reschedule`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось перенести звонок');
    }
    return response.json();
  },

  async cancelCall(organizationId, callId, data) {
    const response = await fetch(`/api/organizations/${organizationId}/calls/${callId}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Не удалось отменить звонок');
    }
    return response.json();
  },
};

/**
 * Форматирование полного имени контакта
 */
const formatContactName = (contact) => {
  if (!contact) return 'Неизвестный контакт';
  const parts = [];
  if (contact.last_name) parts.push(contact.last_name);
  if (contact.first_name) parts.push(contact.first_name);
  return parts.join(' ') || 'Без имени';
};

/**
 * Форматирование имени пользователя
 */
const formatUserName = (user) => {
  if (!user) return 'Неизвестный';
  if (user.full_name) return user.full_name;
  return user.email;
};

/**
 * Форматирование даты и времени
 */
const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Moscow',
  });
};

/**
 * Получение инициалов
 */
const getInitials = (contact) => {
  if (!contact) return 'К';
  const first = contact.first_name?.[0]?.toUpperCase() || '';
  const last = contact.last_name?.[0]?.toUpperCase() || '';
  return last + first || first || 'К';
};

/**
 * Компонент отображения звонка
 */
const CallItem = ({ call, onView, onEdit, onDelete, onComplete, onReschedule, onCancel }) => {
  const contactName = formatContactName(call.contact);
  const callerName = formatUserName(call.caller);
  const status = call.status || 'completed';
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.completed;
  const StatusIcon = statusConfig.icon;
  const isPlanned = status === 'planned';

  // Определяем цвет аватара в зависимости от статуса
  const avatarColor = isPlanned ? 'blue' : (status === 'completed' ? 'green' : 'gray');

  return (
    <Group
      p="sm"
      style={{
        borderRadius: '12px',
        backgroundColor: tokens.colors.neutral[1],
        transition: 'background-color 150ms ease',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[2])}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[1])}
      onClick={() => onView(call)}
    >
      <Avatar size="md" radius="xl" color={avatarColor}>
        {getInitials(call.contact)}
      </Avatar>

      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500} truncate>
            {contactName}
          </Text>
          {call.contact?.position && (
            <Text size="xs" c="dimmed" truncate>
              ({call.contact.position})
            </Text>
          )}
          {/* Бейдж статуса */}
          <Badge
            size="xs"
            color={statusConfig.color}
            variant={isPlanned ? 'filled' : 'light'}
            radius="xl"
            leftSection={<StatusIcon size={10} />}
          >
            {statusConfig.label}
          </Badge>
          {call.is_edited && (
            <Badge
              size="xs"
              color="yellow"
              variant="filled"
              radius="xl"
              leftSection={<IconEdit size={10} />}
            >
              Изменено
            </Badge>
          )}
        </Group>

        {call.subject && (
          <Text size="sm" c="dimmed" truncate>
            {call.subject}
          </Text>
        )}

        <Group gap="md" mt={4}>
          <Text size="xs" c="dimmed">
            <Group gap={4}>
              <IconClock size={12} />
              {formatDateTime(call.call_datetime)}
            </Group>
          </Text>

          <Text size="xs" c="dimmed">
            <Group gap={4}>
              <IconUser size={12} />
              {callerName}
            </Group>
          </Text>
        </Group>

        {/* Показываем результат для выполненных звонков или комментарий */}
        {(call.result || call.comment) && (
          <Text size="xs" c="dimmed" mt={4} lineClamp={2}>
            {call.result || call.comment}
          </Text>
        )}
      </Box>

      {/* Меню действий */}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" onClick={(e) => e.stopPropagation()}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
          {/* Действия для запланированных звонков */}
          {isPlanned && (
            <>
              <Menu.Item
                leftSection={<IconCheck size={14} />}
                color="green"
                onClick={(e) => {
                  e.stopPropagation();
                  onComplete(call);
                }}
              >
                Выполнено
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCalendarEvent size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onReschedule(call);
                }}
              >
                Перенести
              </Menu.Item>
              <Menu.Item
                leftSection={<IconBan size={14} />}
                color="red"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancel(call);
                }}
              >
                Отменить
              </Menu.Item>
              <Menu.Divider />
            </>
          )}
          <Menu.Item
            leftSection={<IconPencil size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(call);
            }}
          >
            Редактировать
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconTrash size={14} />}
            color="red"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(call);
            }}
          >
            Удалить
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
};

/**
 * Основной компонент виджета
 */
const CallsWidget = ({ organization, onActivityChange }) => {
  const [calls, setCalls] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Модальное окно формы
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCall, setEditingCall] = useState(null);

  // Модальное окно просмотра
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewingCall, setViewingCall] = useState(null);

  // Модальное окно причины редактирования
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [pendingEditData, setPendingEditData] = useState(null);

  // Модальные окна для действий с запланированными звонками
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [actionCall, setActionCall] = useState(null);

  // Загрузка звонков
  const loadCalls = useCallback(async () => {
    if (!organization?.id) return;

    try {
      setLoading(true);
      const data = await callsApi.getCalls(organization.id);
      setCalls(data.items || []);
    } catch (err) {
      console.error('Failed to load calls:', err);
    } finally {
      setLoading(false);
    }
  }, [organization?.id]);

  // Загрузка контактов для формы
  const loadContacts = useCallback(async () => {
    if (!organization?.id) return;

    try {
      const data = await callsApi.getContacts(organization.id);
      setContacts(data.items || []);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    }
  }, [organization?.id]);

  useEffect(() => {
    loadCalls();
    loadContacts();
  }, [loadCalls, loadContacts]);

  // Открыть модальное окно для добавления
  const handleAddCall = () => {
    setEditingCall(null);
    setModalOpen(true);
  };

  // Открыть модальное окно для просмотра
  const handleViewCall = (call) => {
    setViewingCall(call);
    setViewModalOpen(true);
  };

  // Открыть модальное окно для редактирования
  const handleEditCall = (call) => {
    setViewModalOpen(false);
    setViewingCall(null);
    setEditingCall(call);
    setModalOpen(true);
  };

  // Удалить звонок
  const handleDeleteCall = async (call) => {
    const contactName = formatContactName(call.contact);
    if (!window.confirm(`Удалить звонок с "${contactName}"?`)) {
      return;
    }

    try {
      await callsApi.deleteCall(organization.id, call.id);
      await loadCalls();
      // Обновляем историю активности
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to delete call:', err);
      alert(err.message);
    }
  };

  // Сохранить звонок (создание или обновление)
  const handleSubmit = async (data) => {
    // Если это редактирование, сначала спрашиваем причину
    if (editingCall) {
      setPendingEditData(data);
      setModalOpen(false);
      setReasonModalOpen(true);
      return;
    }

    // Создание нового звонка
    setActionLoading(true);
    try {
      await callsApi.createCall(organization.id, data);
      setModalOpen(false);
      setEditingCall(null);
      await loadCalls();
      // Обновляем историю активности
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      throw err; // Пробросим ошибку в модальное окно
    } finally {
      setActionLoading(false);
    }
  };

  // Подтверждение редактирования с причиной
  const handleConfirmEdit = async (reason) => {
    if (!editingCall || !pendingEditData) return;

    setActionLoading(true);
    try {
      // Добавляем причину к данным
      const dataWithReason = {
        ...pendingEditData,
        edit_reason: reason,
      };
      await callsApi.updateCall(organization.id, editingCall.id, dataWithReason);
      setReasonModalOpen(false);
      setPendingEditData(null);
      setEditingCall(null);
      await loadCalls();
      // Обновляем историю активности
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to update call:', err);
      alert(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  // Обработчики для действий с запланированными звонками
  const handleCompleteCall = (call) => {
    setActionCall(call);
    setCompleteModalOpen(true);
  };

  const handleRescheduleCall = (call) => {
    setActionCall(call);
    setRescheduleModalOpen(true);
  };

  const handleCancelCall = (call) => {
    setActionCall(call);
    setCancelModalOpen(true);
  };

  // Выполнение звонка
  const handleConfirmComplete = async (data) => {
    if (!actionCall) return;

    setActionLoading(true);
    try {
      await callsApi.completeCall(organization.id, actionCall.id, data);
      setCompleteModalOpen(false);
      setActionCall(null);
      await loadCalls();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to complete call:', err);
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  // Перенос звонка
  const handleConfirmReschedule = async (data) => {
    if (!actionCall) return;

    setActionLoading(true);
    try {
      await callsApi.rescheduleCall(organization.id, actionCall.id, data);
      setRescheduleModalOpen(false);
      setActionCall(null);
      await loadCalls();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to reschedule call:', err);
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  // Отмена звонка
  const handleConfirmCancel = async (data) => {
    if (!actionCall) return;

    setActionLoading(true);
    try {
      await callsApi.cancelCall(organization.id, actionCall.id, data);
      setCancelModalOpen(false);
      setActionCall(null);
      await loadCalls();
      if (onActivityChange) {
        onActivityChange();
      }
    } catch (err) {
      console.error('Failed to cancel call:', err);
      throw err;
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <>
      <BaseWidget
        title="Звонки"
        icon={IconPhone}
        count={calls.length}
        collapsible={true}
        defaultExpanded={calls.length > 0}
        actions={
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={(e) => {
              e.stopPropagation();
              handleAddCall();
            }}
            disabled={contacts.length === 0}
          >
            Добавить
          </Button>
        }
      >
        {loading ? (
          <Center py="lg">
            <Loader size="sm" />
          </Center>
        ) : calls.length > 0 ? (
          <Stack gap="xs">
            {calls.map((call) => (
              <CallItem
                key={call.id}
                call={call}
                onView={handleViewCall}
                onEdit={handleEditCall}
                onDelete={handleDeleteCall}
                onComplete={handleCompleteCall}
                onReschedule={handleRescheduleCall}
                onCancel={handleCancelCall}
              />
            ))}
          </Stack>
        ) : (
          <Box py="lg" style={{ textAlign: 'center' }}>
            <IconPhone
              size={40}
              style={{ color: tokens.colors.neutral[4], marginBottom: '8px' }}
            />
            <Text size="sm" c="dimmed">
              Нет записей о звонках
            </Text>
            <Text size="xs" c="dimmed" mt={4}>
              {contacts.length > 0
                ? 'Запишите результат звонка'
                : 'Сначала добавьте контакты'}
            </Text>
            {contacts.length > 0 && (
              <Button
                variant="light"
                size="xs"
                mt="md"
                leftSection={<IconPlus size={14} />}
                onClick={handleAddCall}
              >
                Добавить звонок
              </Button>
            )}
          </Box>
        )}
      </BaseWidget>

      {/* Модальное окно формы */}
      <CallFormModal
        opened={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingCall(null);
        }}
        onSubmit={handleSubmit}
        call={editingCall}
        organizationId={organization?.id}
        contacts={contacts}
        loading={actionLoading}
      />

      {/* Модальное окно просмотра */}
      <CallViewModal
        opened={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewingCall(null);
        }}
        call={viewingCall}
        onEdit={handleEditCall}
      />

      {/* Модальное окно причины редактирования */}
      <EditReasonModal
        opened={reasonModalOpen}
        onClose={() => {
          setReasonModalOpen(false);
          setPendingEditData(null);
          // Возвращаемся к редактированию
          setModalOpen(true);
        }}
        onConfirm={handleConfirmEdit}
        loading={actionLoading}
        title="Укажите причину изменения звонка"
        entityName="звонок"
      />

      {/* Модальное окно выполнения звонка */}
      <CallCompleteModal
        opened={completeModalOpen}
        onClose={() => {
          setCompleteModalOpen(false);
          setActionCall(null);
        }}
        onSubmit={handleConfirmComplete}
        call={actionCall}
        loading={actionLoading}
      />

      {/* Модальное окно переноса звонка */}
      <CallRescheduleModal
        opened={rescheduleModalOpen}
        onClose={() => {
          setRescheduleModalOpen(false);
          setActionCall(null);
        }}
        onSubmit={handleConfirmReschedule}
        call={actionCall}
        loading={actionLoading}
      />

      {/* Модальное окно отмены звонка */}
      <CallCancelModal
        opened={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setActionCall(null);
        }}
        onSubmit={handleConfirmCancel}
        call={actionCall}
        loading={actionLoading}
      />
    </>
  );
};

export default CallsWidget;
